import { CustomLink } from '@/components/custom-link/CustomLink';
import type { NavbarFolderItem, NavbarItem } from '@/shared/constant/navbar/NavbarItem';
import { Select } from 'aui';
import { FoxIconSort } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import { IconLabel } from './components/icon-label/IconLabel';
import { NavbarElement } from './components/navbar-element/NavbarElement';
import { useNavbarGroupShowCount } from './hooks/useNavbarGroupShowCount';
import styles from './NavbarGroup.module.css';
import { NavbarGroupLevel, NavbarGroupType } from './type/NavbarGroupTypeLevel';

export type NavbarGroupProps =
  | {
      items: NavbarItem[];
      level: NavbarGroupLevel.Level1;
      type: NavbarGroupType.Default | NavbarGroupType.LineTab;
      onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
      className?: string;
      containerClassName?: string;
    }
  | {
      items: NavbarFolderItem[];
      level: NavbarGroupLevel.Level2;
      type: NavbarGroupType.Select | NavbarGroupType.LineTab;
      onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
      className?: string;
      containerClassName?: string;
    }
  | {
      items: NavbarFolderItem[];
      level: NavbarGroupLevel.Level3;
      type: NavbarGroupType.LineTab;
      onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
      className?: string;
      containerClassName?: string;
    };

function NavbarSelectList({
  items,
  className,
  onClick,
}: {
  items: NavbarFolderItem[];
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  const value = React.useMemo(() => {
    return items.find((x) => x.selected)?.id;
  }, [items]);

  return (
    <Select
      className={clsx('w-auto min-w-[120px] max-os:w-full', styles.select, className)}
      value={value}
      options={items.map((x) => {
        return {
          label: (
            <CustomLink to={x.to} disabled={x.disabled} onClick={onClick}>
              <IconLabel
                icon={x.icon}
                label={x.title}
                className="relative top-0 max-w-[192px] text-input max-os:top-[-1px]"
              />
            </CustomLink>
          ),
          value: x.id,
          to: x.to,
          disabled: x.disabled || !x.to,
        };
      })}
      dropdownMatchSelectWidth={false}
      suffixIcon={<FoxIconSort className="text-input" />}
    />
  );
}

export const NavbarGroup = React.memo(function NavbarGroupComponent(props: NavbarGroupProps) {
  const { items, className, onClick, containerClassName, ...restProps } = props;

  const { getContainerRef, showList, moreMenuItem } = useNavbarGroupShowCount(items);

  if (props.type === NavbarGroupType.Select) {
    return (
      <>
        <NavbarSelectList items={props.items} className={containerClassName} onClick={onClick} />
        <ul className="hidden">
          {items.map((x) => {
            return (
              <li key={x.id}>
                <NavbarElement
                  {...props}
                  value={x}
                  isMenuChild={true}
                  groupLevelType={restProps}
                  onClick={onClick}
                />
              </li>
            );
          })}
        </ul>
      </>
    );
  }

  const renderNavbarElementList = (list: NavbarItem[], isShowMore = true) => {
    return (
      <div
        ref={isShowMore ? undefined : getContainerRef}
        className={clsx(
          'flex flex-nowrap items-center gap-2',
          {
            ['max-os:hidden']: isShowMore,
            ['overflow-hidden os:h-0']: !isShowMore,
          },
          'max-os:flex-col max-os:items-start',
          restProps.level !== NavbarGroupLevel.Level1 ? 'max-os:gap-[6px]' : 'max-os:gap-[1px]',
          className,
        )}
      >
        {list.map((x) => {
          return (
            <NavbarElement
              key={x.id}
              value={x}
              groupLevelType={restProps}
              isMenuChild={false}
              onClick={onClick}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className={clsx('', containerClassName)}>
      {renderNavbarElementList(items, false)}
      {renderNavbarElementList([...showList, ...(moreMenuItem ? [moreMenuItem] : [])], true)}
    </div>
  );
});
